"use client";

// invokeCommand is the imperative entry point that everything —
// the palette, the global hotkey binder, and the right-click
// context menu — funnels through. It centralises:
//
//   1. Routing for `navigate` commands.
//   2. Cross-tree panel mounting via a global event picked up by
//      <CommandPanelHost />. Panels can't render from inside a
//      handler closure (no React tree) so we surface them through
//      a DOM event and let the host render them in-tree.
//   3. Mutation execution with try/catch + telemetry.
//   4. Telemetry emission on every invocation, regardless of kind.
//
// Confirmation handling lives in the palette layer, not here:
// we want the AlertDialog to render inside React, so the palette
// renders <ConfirmDialog> and only then calls invokeCommand
// (sans confirmation).

import type {
  Command,
  CommandContext,
  PanelComponent,
} from "@/lib/commands/types";
import { emitTelemetry } from "@/lib/commands/telemetry";

export const COMMAND_PANEL_EVENT = "command-bar:open-panel";

export interface OpenPanelDetail {
  panel: PanelComponent;
  ctxSnapshot: CommandContext;
}

export function dispatchOpenPanel(detail: OpenPanelDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(COMMAND_PANEL_EVENT, { detail }));
}

export type CommandSource = "palette" | "hotkey" | "context_menu" | "shortcut_page";

export async function invokeCommand(
  cmd: Command,
  ctx: CommandContext,
  source: CommandSource = "palette",
): Promise<void> {
  const start = performance.now();
  let succeeded = true;
  let errorCode = "";
  try {
    switch (cmd.handler.kind) {
      case "navigate": {
        const href =
          typeof cmd.handler.href === "function"
            ? cmd.handler.href(ctx)
            : cmd.handler.href;
        ctx.router.push(href);
        return;
      }
      case "panel": {
        dispatchOpenPanel({ panel: cmd.handler.open, ctxSnapshot: ctx });
        return;
      }
      case "mutation": {
        await cmd.handler.run(ctx);
        return;
      }
    }
  } catch (e) {
    succeeded = false;
    errorCode = e instanceof Error ? e.name || "error" : "error";
    // Surfacing rethrows to the caller for hotkey/context-menu
    // surfaces that may want to react. The palette already
    // displays toast messages from the handler; we don't add
    // a second one here.
    throw e;
  } finally {
    emitTelemetry({
      command_id: cmd.id,
      source,
      context_scope: cmd.scope,
      duration_ms: Math.round(performance.now() - start),
      succeeded,
      error_code: errorCode,
    });
  }
}
